import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Form, Input, Select, Button, Typography, Result, Spin, message } from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
import axios from "axios";
import {getBaseURL} from "../../services/api";
import futezapLogo from '../../assets/futezap.jpg';

const api = axios.create({
  baseURL: getBaseURL(),
});

const { Title, Text } = Typography;
const { Option } = Select;

const cardTitle = (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
    <img src={futezapLogo} alt="Futezap" style={{ height: 40, width: 40, borderRadius: 4 }} />
    <span>Futezap</span>
  </div>
);

const pageWrapperStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #00B96B 0%, #52C41A 100%)',
  padding: 16,
};

const PublicPlayerRegister = () => {
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [teamData, setTeamData] = useState(null);
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    const fetchTeamInfo = async () => {
      try {
        const response = await api.get(`/public-register/${code}`);
        setTeamData(response.data);
        if (response.data.public_register && response.data.token) {
          setToken(response.data.token);
        }
      } catch (err) {
        setError('Código inválido ou time não encontrado');
      }
      setLoading(false);
    };
    fetchTeamInfo();
  }, [code]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const res = await api.post(
        `/public-register/player`,
        {
          name: values.name,
          phone: values.phone,
          position: values.position
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (res.data?.already_registered) {
        setAlreadyRegistered(true);
      } else {
        setSuccess(true);
      }
    } catch (err) {
      if (err.response?.data?.message === 'TOKEN_EXPIRED') {
        setError('O tempo para cadastro expirou, recarregue a página para tentar novamente');
      } else {
        message.error('Ocorreu um problema ao tentar realizar seu cadastro, tente novamente em alguns minutos ou notifique o administrador do time');
      }
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={pageWrapperStyle}>
        <Card title={cardTitle} style={{ textAlign: 'center', maxWidth: 400 }}>
          <Spin size="large" />
          <Text style={{ display: 'block', marginTop: 16 }}>Aguarde enquanto carregamos os dados necessários...</Text>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageWrapperStyle}>
        <Card title={cardTitle} style={{ maxWidth: 500 }}>
          <Result status="warning" title={error} />
        </Card>
      </div>
    );
  }

  if (teamData && !teamData.public_register) {
    return (
      <div style={pageWrapperStyle}>
        <Card title={cardTitle} style={{ maxWidth: 500 }}>
          <Result
            status="info"
            title={`O time ${teamData.game_name} não está aceitando inscrições neste momento`}
          />
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div style={pageWrapperStyle}>
        <Card title={cardTitle} style={{ maxWidth: 500 }}>
          <Result
            status="success"
            title="Jogador, seu cadastro foi realizado!"
          />
        </Card>
      </div>
    );
  }

  if (alreadyRegistered) {
    return (
      <div style={pageWrapperStyle}>
        <Card title={cardTitle} style={{ maxWidth: 500 }}>
          <Result
            status="info"
            title="Você já está cadastrado neste time"
            subTitle="Não é necessário se cadastrar novamente. Fique atento às próximas convocações no grupo."
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={pageWrapperStyle}>
      <Card title={cardTitle} style={{ width: '100%', maxWidth: 480 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <UserAddOutlined style={{ fontSize: 40, color: '#1890ff' }} />
          <Title level={3} style={{ marginTop: 8, marginBottom: 4 }}>Cadastro de Jogador</Title>
          <Text type="secondary">{teamData.game_name}</Text>
        </div>

        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item
            name="name"
            label="Nome"
            rules={[
              { required: true, message: 'Nome é obrigatório' },
              { min: 3, message: 'Nome deve ter no mínimo 3 caracteres' }
            ]}
          >
            <Input placeholder="Seu nome" disabled={submitting} />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Telefone"
            rules={[
              { required: true, message: 'Telefone é obrigatório' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const digits = value.replace(/\D/g, '');
                  if (digits.length !== 10 && digits.length !== 11) {
                    return Promise.reject('Telefone inválido. Use DDD + número (10 ou 11 dígitos)');
                  }
                  if (digits.length === 11 && digits[2] !== '9') {
                    return Promise.reject('Telefone com 11 dígitos deve ter o 9 na terceira posição');
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input placeholder="Mesmo do seu WhatsApp" disabled={submitting} maxLength={11} />
          </Form.Item>

          <Form.Item
            name="position"
            label="Posição"
            rules={[{ required: true, message: 'Posição é obrigatória' }]}
          >
            <Select placeholder="Selecione sua posição" disabled={submitting}>
              <Option value="1">Jogador de Linha</Option>
              <Option value="2">Goleiro</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={submitting} disabled={submitting}>
              Cadastrar
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default PublicPlayerRegister;
